import { Injectable } from '@angular/core';

const KEY = 'pt_tools_dismissed';

/**
 * The tools she said "not for me" to (Danna 22.09).
 *
 * ONE source of truth, because the refusal is made on the home strip but has
 * to be visible and REVERSIBLE from the tools list: "it should be possible to
 * bring a tool back from the list even if it was marked not for me". Two
 * private copies of this set would have let the two screens disagree.
 *
 * Device-local on purpose: a refusal is a preference, not a server fact, and
 * it should never outlive the phone it was made on.
 */
@Injectable({ providedIn: 'root' })
export class DismissedToolsService {
  private ids = new Set<string>();
  private loaded = false;

  private load(): void {
    if (this.loaded) return;
    this.loaded = true;
    try {
      const raw = localStorage.getItem(KEY);
      const list = raw ? JSON.parse(raw) : [];
      if (Array.isArray(list)) {
        this.ids = new Set(list.filter((v): v is string => typeof v === 'string'));
      }
    } catch {
      /* a corrupt list just means nothing is dismissed */
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(KEY, JSON.stringify([...this.ids]));
    } catch {
      /* storage blocked: the refusal holds for this visit only */
    }
  }

  all(): string[] {
    this.load();
    return [...this.ids];
  }

  has(id: string): boolean {
    this.load();
    return this.ids.has(id);
  }

  dismiss(id: string): void {
    this.load();
    this.ids.add(id);
    this.persist();
  }

  /** Bring it back: from the undo on the card, or from the tools list. */
  restore(id: string): void {
    this.load();
    this.ids.delete(id);
    this.persist();
  }
}
